import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Avatar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MedicationIcon from "@mui/icons-material/Medication";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import VitalsPanel from "../components/VitalsPanel";
import ActivityCard from "./components/ActivityCard";
import { logActivity } from "../utils/ActivityLog";

/* ================= TYPES ================= */

interface Appointment {
  id: string;
  appointment_date: string;
  appointment_time: string;
  reason_for_visit: string;
  status: string;
  provider: {
    provider: {
      first_name: string;
      last_name: string;
      specialization: string;
      contact_number: string;
    };
  };
}

/* ================= CONSTANT ================= */

const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

/* ================= COMPONENT ================= */

export const PatientDashboardLayout = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [latestAppointment, setLatestAppointment] =
    useState<Appointment | null>(null);
  const [loading, setLoading] = useState(false);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const token =
    (user as any)?.token || localStorage.getItem("token") || "";

  /* ================= FETCH UPCOMING ================= */

  const fetchUpcomingAppointments = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/appointments?limit=100&page=1&filter_type=upcoming`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data: Appointment[] = res.data?.data || [];

      if (data.length) {
        const sorted = [...data].sort(
          (a, b) =>
            new Date(a.appointment_date).getTime() -
            new Date(b.appointment_date).getTime()
        );
        setLatestAppointment(sorted[0]);
      } else {
        setLatestAppointment(null);
      }
    } catch (error) {
      console.error("Failed to fetch appointments", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUpcomingAppointments();
  }, [fetchUpcomingAppointments]);

  /* ================= CANCEL ================= */

  const confirmCancel = async () => {
    if (!latestAppointment || !cancelReason.trim()) return;

    try {
      await axios.put(
        `${BASE_URL}/appointments/${latestAppointment.id}`,
        {
          status: "CANCELLED",
          cancellation_reason: cancelReason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Log the cancellation activity
      logActivity(
        user?.id ? String(user.id) : undefined,
        "appointment_cancelled",
        `Cancelled appointment with Dr. ${latestAppointment.provider.provider.first_name} ${latestAppointment.provider.provider.last_name}`,
        { appointmentId: latestAppointment.id, reason: cancelReason }
      );

      toast.success("Appointment cancelled");
      setCancelDialogOpen(false);
      setCancelReason("");
      fetchUpcomingAppointments();
    } catch (error) {
      toast.error("Failed to cancel appointment");
    }
  };

  /* ================= RESCHEDULE (YOUR APPROACH) ================= */

  const handleReschedule = async () => {
    if (!latestAppointment) return;

    try {
      await axios.put(
        `${BASE_URL}/appointments/${latestAppointment.id}`,
        {
          appointment_date: dayjs().add(2, "day").format("YYYY-MM-DD"),
          appointment_time: "10:00:00",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Log the reschedule activity
      const newDate = dayjs().add(2, "day").format("YYYY-MM-DD");
      logActivity(
        user?.id ? String(user.id) : undefined,
        "appointment_rescheduled",
        `Rescheduled appointment with Dr. ${latestAppointment.provider.provider.first_name} ${latestAppointment.provider.provider.last_name} to ${newDate}`,
        { appointmentId: latestAppointment.id, newDate, newTime: "10:00:00" }
      );

      toast.success("Appointment rescheduled");
      fetchUpcomingAppointments();
    } catch (error) {
      toast.error("Failed to reschedule appointment");
    }
  };

  /* ================= UI ================= */

  return (
    <div className="section" style={{ width: "100%" }}>
      <div className="heading" style={{ marginBottom: 20 }}>
        Dashboard
      </div>

      <Box>
        {/* ================= ROW 1 ================= */}
        <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
          {/* ================= NEXT APPOINTMENT ================= */}
          <Paper sx={{ flex: "1 1 300px", p: 2 }}>
            <Typography fontWeight="bold">Next Appointment</Typography>

            {loading ? (
              <Box display="flex" justifyContent="center" mt={3}>
                <CircularProgress size={24} />
              </Box>
            ) : latestAppointment ? (
              <>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    {latestAppointment.provider.provider.first_name[0]}
                  </Avatar>
                  <Box>
                    <Typography fontWeight={600}>
                      Dr.{" "}
                      {latestAppointment.provider.provider.first_name}{" "}
                      {latestAppointment.provider.provider.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {latestAppointment.provider.provider.specialization}
                    </Typography>
                  </Box>
                </Box>

                <Box mt={2}>
                  <Typography variant="body2">
                    📅{" "}
                    {new Date(
                      latestAppointment.appointment_date
                    ).toDateString()}
                  </Typography>
                  <Typography variant="body2">
                    🕒 {latestAppointment.appointment_time}
                  </Typography>
                  <Typography variant="body2">
                    📞{" "}
                    {
                      latestAppointment.provider.provider
                        .contact_number
                    }
                  </Typography>
                  <Typography variant="body2">
                    📝 {latestAppointment.reason_for_visit}
                  </Typography>
                </Box>

                <Box display="flex" gap={2} mt={2}>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancel
                  </Button>
                  <Button size="small" onClick={handleReschedule}>
                    Reschedule
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                mt={3}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
              >
                <Typography fontWeight={600} color="text.secondary">
                  Currently no appointment scheduled
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Book an appointment to see it here.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => navigate("/user-layout/doctor-list")}
                >
                  Book Appointment
                </Button>
              </Box>
            )}
          </Paper>

          {/* ================= SCHEDULE ================= */}
          <Paper sx={{ flex: "1 1 300px", p: 2 }}>
            <Typography fontWeight="bold" mb={1}>
              <CalendarMonthIcon fontSize="small" /> Schedule
            </Typography>
            <Typography variant="body2">
              Calendar view coming soon
            </Typography>
            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => navigate("/user-layout/doctor-list")}
            >
              Find a Doctor
            </Button>
          </Paper>

          {/* ================= ACTIVITY LOG ================= */}
          <ActivityCard patientId={user?.id ? String(user.id) : undefined} limit={5} />
        </Box>

        {/* ================= VITALS ================= */}
        <Box sx={{ mb: 2 }}>
          <VitalsPanel />
        </Box>

        {/* ================= BOTTOM CARDS ================= */}
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Paper sx={{ flex: "1 1 300px", p: 2 }}>
            <Typography fontWeight="bold" mb={1}>
              <MedicationIcon fontSize="small" /> Medications
            </Typography>
            <Typography variant="body2">
              No medications added yet
            </Typography>
          </Paper>

          <Paper sx={{ flex: "1 1 300px", p: 2 }}>
            <Typography fontWeight="bold" mb={1}>
              <LocalHospitalIcon fontSize="small" /> Reports
            </Typography>
            <Button
              variant="text"
              fullWidth
              onClick={() => navigate("/user-layout/my-health")}
            >
              + Add Reports
            </Button>
          </Paper>
        </Box>
      </Box>

      {/* ================= CANCEL DIALOG ================= */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for cancellation"
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Close
          </Button>
          <Button
            color="error"
            onClick={confirmCancel}
            disabled={!cancelReason.trim()}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
